import { observer } from 'mobx-react-lite';

import Link from 'next/link';
import type {
  ClaimLicenseBannerAnalyst,
  ClaimLicenseBannerDisplayResponse,
} from 'types/claim-license';

import { Spacer, makeStyles } from '@bequestinc/wui';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';

const useStyles = makeStyles()({
  alert: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    boxSizing: 'border-box',
    alignItems: 'flex-start',
    justifyContent: 'center',
    '& .MuiAlert-message': {
      flex: '0 1 auto',
      width: 'auto',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  content: {
    width: 'auto',
    maxWidth: '100%',
    alignItems: 'flex-start',
    textAlign: 'left',
  },
  analystList: {
    margin: 0,
    padding: 0,
    listStylePosition: 'inside',
    textAlign: 'left',
  },
  details: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'flex-start',
    width: '100%',
    whiteSpace: 'nowrap',
  },
});

const formatStates = (states: string[]) => {
  if (states.length < 2) {
    return states.join('');
  }

  if (states.length === 2) {
    return states.join(' and ');
  }

  const lastState = states[states.length - 1];
  const precedingStates = states.slice(0, -1).join(', ');

  return `${precedingStates}, and ${lastState}`;
};

const formatLicensePhrase = (states: string[], status: string) => {
  const licenseCount = states.length;
  const licenseWord = licenseCount === 1 ? 'license' : 'licenses';
  const article = licenseCount === 1 ? 'an ' : '';

  return `${article}${status} ${licenseWord} in ${formatStates(states)}`;
};

const getAnalystExpirationStatus = ({
  name,
  expiredStates,
  expiringStates,
}: ClaimLicenseBannerAnalyst) => {
  const phrases = [];

  if (expiredStates.length > 0) {
    phrases.push(formatLicensePhrase(expiredStates, 'expired'));
  }

  if (expiringStates.length > 0) {
    phrases.push(formatLicensePhrase(expiringStates, 'expiring'));
  }

  return `${name} has ${phrases.join(' and ')}`;
};

const getClaimUserExpirationStatus = ({
  expiredStates,
  expiringStates,
}: Pick<ClaimLicenseBannerDisplayResponse, 'expiredStates' | 'expiringStates'>) => {
  const messages = [];

  if (expiredStates.length > 0) {
    messages.push(
      `Your ${expiredStates.length === 1 ? 'license' : 'licenses'} in ${formatStates(
        expiredStates,
      )} ${expiredStates.length === 1 ? 'has' : 'have'} expired`,
    );
  }

  if (expiringStates.length > 0) {
    const prefix = expiredStates.length > 0 ? 'your' : 'Your';
    messages.push(
      `${prefix} ${expiringStates.length === 1 ? 'license' : 'licenses'} in ${formatStates(
        expiringStates,
      )} ${expiringStates.length === 1 ? 'is' : 'are'} expiring soon`,
    );
  }

  return messages.join(' and ');
};

interface PropTypes {
  bannerData: ClaimLicenseBannerDisplayResponse;
  canViewLicenseBanner: boolean;
  isClaimsManager: boolean;
}

const ExpiredLicenseBanner = ({ bannerData, canViewLicenseBanner, isClaimsManager }: PropTypes) => {
  const { classes } = useStyles();

  if (
    !canViewLicenseBanner ||
    (bannerData.expiredStates.length === 0 &&
      bannerData.expiringStates.length === 0 &&
      (!isClaimsManager || bannerData.analysts.length === 0))
  ) {
    return null;
  }
  return (
    <>
      <Spacer v={15} />
      <Alert severity="warning" className={classes.alert}>
        {isClaimsManager ? (
          <Stack className={classes.content}>
            The following claims analysts have licenses that are expired or expiring:
            <ul className={classes.analystList}>
              {bannerData.analysts.length > 0 &&
                bannerData.analysts.map(analyst => (
                  <li key={analyst.id}>{getAnalystExpirationStatus(analyst)}</li>
                ))}
            </ul>
          </Stack>
        ) : (
          <Stack className={classes.content}>{getClaimUserExpirationStatus(bannerData)}.</Stack>
        )}
        <Stack className={classes.details}>
          Click <Link href={isClaimsManager ? '/claim-license' : '/my-info'}>here</Link> to view
          details
        </Stack>
      </Alert>
    </>
  );
};

export default observer(ExpiredLicenseBanner);
